<script>
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import axios from "axios";
import Swal from "sweetalert2";
import MultiSelect from 'primevue/multiselect';

export default {
  name: 'BulkSms',
  components: {
    Dialog,
    Dropdown,
    MultiSelect
  },
  props: {
    academic_year_id: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      visible: false,
      formSending: false,
      attendance_message: "",
      reservations: [],
      students: [],
      recipientMode: 'guardian',
      recipientModes: [
        { id: 'guardian', title: 'رقم ولي الأمر' },
        { id: 'student', title: 'رقم الطالب' },
        { id: 'both', title: 'كلاهما' },
      ],
      // No default — the admin must explicitly pick one. Whichever is
      // selected is the ONLY channel used; there is no auto-fallback from
      // one to the other any more.
      channel: null,
      skippedStudents: [],
      status: null,
      status_list: [
        {
          id: 0,
          title: "معلق",
        },
        {
          id: 1,
          title: "مقبول",
        },
        {
          id: 2,
          title: "مرفوض",
        },
        {
          id: 3,
          title: "في انتظار الشهادة",
        }
      ],
    };
  },
  computed: {
    smsCount() {
      return Math.ceil(this.attendance_message.length / 70) || 1;
    }
  },
  methods: {
    openDialog() {
      this.recipientMode = 'guardian';
      this.channel = null;
      this.skippedStudents = [];
      this.visible = true;
    },
    fetchReservation(page = 1) {
      const formData = new FormData();
      if (this.academic_year_id) formData.append('academic_year_id', this.academic_year_id);

      if (this.status) {
        formData.append('status', this.status.id);
      }

      const pageNumber = Number.isInteger(page) ? page : (parseInt(page, 10) || 1);

      axios.defaults.headers.common["per_page"] = 10000000;
      axios
          .post(`/all_reservations?page=${pageNumber}&per_page=10000`, formData)
          .then((response) => {
            this.reservations = response.data.data.data;
            this.paggination = response.data.data.meta.total;
          })
          .catch((error) => {
            this.reservations = {};
          });
    },


    async SendAttendanceMessage() {
      if (this.formSending) return;
      if (!this.recipientModes.some(mode => mode.id === this.recipientMode) ||
          this.students.length === 0 || !this.attendance_message.trim()) {
        Swal.fire({ icon: 'warning', title: 'تنبيه', text: 'اختر الطلاب ومستلم الرسالة وأدخل نص الرسالة' });
        return;
      }
      if (!this.channel) {
        Swal.fire({ icon: 'warning', title: 'تنبيه', text: 'يرجى اختيار طريقة الإرسال: WhatsApp أو SMS' });
        return;
      }
      this.formSending = true;
      this.skippedStudents = [];

      try {
        // Single backend call — resolves recipients AND sends, through
        // ONLY the admin-selected channel (WhatsApp/SmsService server-side,
        // never a direct-from-browser provider call). No auto-fallback: if
        // the chosen channel can't be used, this fails outright rather than
        // silently trying the other one.
        const { data } = await axios.post('/whatsapp/bulk-send', {
          student_ids: [...new Set(this.students.map(item => item.user_id))],
          recipient_mode: this.recipientMode,
          channel: this.channel,
          message: this.attendance_message,
        });
        this.skippedStudents = data.skipped_students ?? [];

        Swal.fire({
          icon: data.success ? 'success' : 'error',
          title: data.success ? 'تم الإرسال' : 'تعذر الإرسال',
          text: data.success
            ? `تم إرسال الرسالة إلى ${data.total_recipients} مستلم عبر ${this.channel === 'whatsapp' ? 'واتساب' : 'SMS'}`
            : (data.message || 'حدث خطأ غير متوقع'),
          timer: data.success ? 2000 : undefined,
          showConfirmButton: !data.success,
        });
      } catch (error) {
        Swal.fire({
          icon: 'error', title: 'خطأ',
          text: error.response?.data?.message ?? 'تعذر إرسال الرسالة. حاول مرة أخرى',
        });
      } finally {
        this.formSending = false;
      }
    }
  }
};
</script>

<template>
  <button class="btn PrimaryButton" type="button" @click="openDialog">رسائل جماعية</button>


  <Dialog v-model:visible="visible" modal header="رسائل جماعية" :style="{ width: '35rem' }">
    <div class="row">
      <div class="col-12">
        <div class="form-group d-flex flex-column">
          <label for="status">حالة الحجز</label>
          <Dropdown v-model="status" :options="status_list" showClear optionLabel="title"
                    placeholder="حالة الحجز" @change="fetchReservation" />
        </div>
      </div>
      <div class="col-12 mt-3" v-if="reservations">
        <div class="form-group d-flex flex-column">
          <label for="status">الطلاب</label>
          <MultiSelect v-model="students" :options="reservations" optionLabel="name" filter placeholder="الطلاب"
                       :maxSelectedLabels="3" class="w-full md:w-100" />

        </div>
      </div>
      <div class="col-12 mt-3">
        <div class="form-group d-flex flex-column">
          <label for="bulk-recipient-mode">إرسال الرسالة إلى</label>
          <Dropdown inputId="bulk-recipient-mode" v-model="recipientMode" :options="recipientModes"
                    optionLabel="title" optionValue="id" :disabled="formSending" />
        </div>
      </div>
      <div class="col-12 mt-3">
        <div class="form-group d-flex flex-column">
          <label>طريقة الإرسال <span class="text-danger">*</span></label>
          <div class="btn-group" role="group">
            <button type="button" class="btn" :class="channel === 'whatsapp' ? 'btn-success' : 'btn-outline-success'"
              :disabled="formSending" @click="channel = 'whatsapp'">
              <i class="pi pi-whatsapp me-1"></i>WhatsApp
            </button>
            <button type="button" class="btn" :class="channel === 'sms' ? 'btn-primary' : 'btn-outline-primary'"
              :disabled="formSending" @click="channel = 'sms'">
              <i class="pi pi-comment me-1"></i>SMS
            </button>
          </div>
        </div>
      </div>
      <div v-if="skippedStudents.length" class="col-12 mt-3">
        <div class="alert alert-warning mb-0" role="alert">
          تم تخطي الطلاب التاليين لعدم وجود رقم صالح للمستلم المحدد أو لعدم توفر سجل الطالب:
          <ul class="mb-0 mt-1">
            <li v-for="student in skippedStudents" :key="student.user_id">{{ student.name || `#${student.user_id}` }}</li>
          </ul>
        </div>
      </div>
      <div class="col-12 mt-3">
        <textarea class="form-control" rows="5" v-model="attendance_message" placeholder="اكتب الرسالة هنا"></textarea>
        <div class="text-end mt-1" :class="attendance_message.length > 70 ? 'text-danger' : 'text-success'">
          {{ attendance_message.length }} حرف
          <span class="ms-2">عدد الرسائل: {{ smsCount }}</span>
        </div>
<!--        <span :class="attendanceMessageLength > 70 ? 'text-danger' : 'text-success'">{{ attendanceMessageLength }}</span>-->
      </div>
      <div class="col-12 mt-4">
        <button class="PrimaryButton w-100" type="button" :disabled="formSending || !channel" @click="SendAttendanceMessage"><i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i> {{ formSending ? 'جاري الارسال...' : 'إرسال' }} </button>
      </div>
    </div>
  </Dialog>

</template>

<style scoped>

</style>